/**
 * Loading state for Genre Jukebox forms (contact form, footer waitlist form).
 *
 * Add `data-gj-loading` to the <form> and give the submit button two children:
 *   <span class="gj-button__label" data-loading-text="Sending...">Send</span>
 *   <span class="gj-button__spinner" aria-hidden="true"></span>
 *
 * On submit (which only fires once the browser's own validation has passed) the button is disabled,
 * its label switches to `data-loading-text` and the spinner shows (CSS: `.gj-button[aria-busy='true']`).
 * A second submit is blocked. The state stays until the page reloads; when the browser restores the
 * page from the back/forward cache it is reset.
 *
 * Shopify's storefront spam protection (hCaptcha; it marks the form with `data-hcaptcha-bound`) cancels
 * the submit event, runs its check and then posts the form itself with the native form.submit(), which
 * fires no second submit event. So for those forms the loader starts even though the event was
 * cancelled, and if the post never happens (the challenge was closed) the button comes back after
 * CAPTCHA_RESET_MS.
 *
 * Loaded as a module, so including it from several sections still runs it once.
 */

/** Shopify's hCaptcha script sets this attribute on the forms it protects */
const CAPTCHA_ATTRIBUTE = 'data-hcaptcha-bound';
const CAPTCHA_RESET_MS = 30000;

/** @type {WeakMap<HTMLFormElement, number>} */
const resetTimers = new WeakMap();

/** @param {HTMLFormElement} form */
function startLoading(form) {
  form.dataset.gjSubmitting = 'true';
  form.setAttribute('aria-busy', 'true');

  for (const button of form.querySelectorAll('button[type="submit"], button:not([type])')) {
    if (!(button instanceof HTMLButtonElement)) continue;

    const label = button.querySelector('.gj-button__label');
    if (label instanceof HTMLElement && label.dataset.loadingText) {
      label.dataset.idleText = label.textContent ?? '';
      label.textContent = label.dataset.loadingText;
    }
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
  }
}

/** @param {HTMLFormElement} form */
function stopLoading(form) {
  clearTimeout(resetTimers.get(form));
  resetTimers.delete(form);
  delete form.dataset.gjSubmitting;
  form.removeAttribute('aria-busy');

  for (const button of form.querySelectorAll('button[aria-busy="true"]')) {
    if (!(button instanceof HTMLButtonElement)) continue;

    const label = button.querySelector('.gj-button__label');
    if (label instanceof HTMLElement && label.dataset.idleText !== undefined) {
      label.textContent = label.dataset.idleText;
      delete label.dataset.idleText;
    }
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-gj-loading')) return;

  if (form.dataset.gjSubmitting === 'true') {
    event.preventDefault();
    return;
  }
  const heldByCaptcha = event.defaultPrevented && form.hasAttribute(CAPTCHA_ATTRIBUTE);
  // Some other script cancelled this submit for its own reasons: leave the button alone
  if (event.defaultPrevented && !heldByCaptcha) return;

  startLoading(form);
  if (heldByCaptcha) resetTimers.set(form, window.setTimeout(() => stopLoading(form), CAPTCHA_RESET_MS));
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const form of document.querySelectorAll('form[data-gj-loading]')) {
    if (form instanceof HTMLFormElement) stopLoading(form);
  }
});
